import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { 
    CurrentBrand, 
    SpotifyArtistSearchResponse
} from 'modules/Types';
import useBrand from './useBrand';
import { createBrand_api, CreateBrandPayload } from 'api/brandApi'; // Assuming this API and type exist
import { useHistory } from 'react-router-dom';
import Axios from 'helpers/Interceptor'; // Added for temporary brand linking
import useUser from './useUser'; // Added for temporary brand linking

// Define OnboardingStepV3 if it's not already defined elsewhere
export enum OnboardingStepV3 {
    BRAND_NAME = 'BRAND_NAME',
    PLATFORM_CONNECTIONS = 'PLATFORM_CONNECTIONS',
    QUALIFYING_QUESTIONS = 'QUALIFYING_QUESTIONS',
    COMPLETED = 'COMPLETED',
}

interface OnboardingStateV3 {
    // Step 1
    brandName: string;
    setBrandName: (name: string) => void;

    // Step 2 - Platform Connections
    selectedConnections: {
        shopify?: { url: string };
        etsy?: { url: string };
        newsletter?: { url: string };
        website?: { url: string };
        twitter?: { url: string };
        spotify?: SpotifyArtistSearchResponse;
        instagram?: { url: string };
        tiktok?: { url: string };
    };
    setSelectedConnections: (connections: OnboardingStateV3['selectedConnections']) => void;
    updateSingleConnection: (platform: keyof OnboardingStateV3['selectedConnections'], data: any) => void;


    // Step 3 - Qualifying Questions
    hearAboutUs: string;
    monthlyMarketingSpend: string;
    teamSize: 'just_me' | 'duo' | 'small_team' | 'label' | 'agency' | '';
    setHearAboutUs: (value: string) => void;
    setMonthlyMarketingSpend: (value: string) => void;
    setTeamSize: (value: OnboardingStateV3['teamSize']) => void;

    currentStep: OnboardingStepV3;
    setCurrentStep: (step: OnboardingStepV3) => void;

    isLoading: boolean;
    error: string | null;

    // Actions
    createBrandOnboarding: () => Promise<CurrentBrand | null>;
    validateStep: (step: OnboardingStepV3) => boolean;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    submitOnboarding: () => Promise<void>;
    resetOnboardingState: () => void;
}

const initialState = {
    brandName: '',
    selectedConnections: {},
    hearAboutUs: '',
    monthlyMarketingSpend: '',
    teamSize: '' as OnboardingStateV3['teamSize'],
    currentStep: OnboardingStepV3.BRAND_NAME,
    isLoading: false,
    error: null,
};

const useOnboardingV3 = create(
    immer<OnboardingStateV3>((set, get) => ({
        ...initialState,

        setBrandName: (name) => set({ brandName: name }),
        setSelectedConnections: (connections) => set({ selectedConnections: connections }),
        updateSingleConnection: (platform, data) => {
            set(state => {
                state.selectedConnections[platform] = data;
            });
        },
        setHearAboutUs: (value) => set({ hearAboutUs: value }),
        setMonthlyMarketingSpend: (value) => set({ monthlyMarketingSpend: value }),
        setTeamSize: (value) => set({ teamSize: value }),
        setCurrentStep: (step) => set({ currentStep: step }),

        createBrandOnboarding: async () => {
            set({ isLoading: true, error: null });
            const { brandName, selectedConnections, hearAboutUs, monthlyMarketingSpend, teamSize } = get();
            const { 
                createBrand, 
                setCurrentBrand, 
                isAnonymousBrand, 
                temporarySessionId,
                getOrCreateAnonymousSessionId,
                clearTemporaryBrand 
            } = useBrand.getState();
            const { currentUser } = useUser.getState();

            const payload: CreateBrandPayload = {
                name: brandName,
                customization: {
                    // Map selectedConnections to the format expected by CreateBrandPayload
                    shopify: selectedConnections.shopify ? { url: selectedConnections.shopify.url } : undefined,
                    etsy: selectedConnections.etsy ? { url: selectedConnections.etsy.url } : undefined,
                    newsletter: selectedConnections.newsletter ? { url: selectedConnections.newsletter.url } : undefined,
                    website: selectedConnections.website ? { url: selectedConnections.website.url } : undefined,
                    twitter: selectedConnections.twitter ? { url: selectedConnections.twitter.url } : undefined,
                    spotify_artist: selectedConnections.spotify || undefined,
                    business_discovery_instagram: selectedConnections.instagram ? { 
                        url: selectedConnections.instagram.url,
                        username: selectedConnections.instagram.url.split('/').pop() || ''
                    } : undefined,
                    // tiktok requires full profile object, not just URL - omitting for now

                    v2_onboarding_answers: {
                        heard_about_us: hearAboutUs,
                        monthly_marketing_spend: monthlyMarketingSpend,
                        team_size: teamSize,
                    },
                    isGrandfathered: false, // New brands are never grandfathered
                },
            };

            try {
                const newBrand = await createBrand(payload); // Using useBrand's createBrand
                if (newBrand) {
                    // If the user was anonymous and had a temporary brand, link it to this new brand
                    if (isAnonymousBrand && temporarySessionId && currentUser?.id) {
                        try {
                            const response = await Axios.post('/temporary-brand/link-to-user', {
                                sessionId: temporarySessionId,
                                userId: currentUser.id
                            });
                            
                            if (response.data?.brand) {
                                console.log('✅ Successfully linked anonymous brand to new user brand');
                                // Clear temporary brand state
                                clearTemporaryBrand();
                            }
                        } catch (linkError) {
                            console.error('Failed to link anonymous brand to new user brand:', linkError);
                            // Don't fail the onboarding if linking fails
                        }
                    }
                    
                    setCurrentBrand(newBrand); // Set this new brand as current
                    set({ isLoading: false, currentStep: OnboardingStepV3.COMPLETED });
                    return newBrand;
                }
                throw new Error("Brand creation failed");
            } catch (e: any) {
                set({ isLoading: false, error: e.message || 'Failed to create brand' });
                return null;
            }
        },

        validateStep: (step) => {
            const { brandName, hearAboutUs } = get();
            switch (step) {
                case OnboardingStepV3.BRAND_NAME:
                    return brandName.trim().length > 0;
                case OnboardingStepV3.PLATFORM_CONNECTIONS:
                    return true; // Optional step
                case OnboardingStepV3.QUALIFYING_QUESTIONS:
                    return hearAboutUs.trim().length > 0; // 'How did you hear about us?' is required
                default:
                    return true;
            }
        },

        goToNextStep: () => {
            const { currentStep, validateStep } = get();
            if (!validateStep(currentStep)) {
                // Optionally set an error message for the UI
                return;
            }

            let nextStep = currentStep;
            if (currentStep === OnboardingStepV3.BRAND_NAME) nextStep = OnboardingStepV3.PLATFORM_CONNECTIONS;
            else if (currentStep === OnboardingStepV3.PLATFORM_CONNECTIONS) nextStep = OnboardingStepV3.QUALIFYING_QUESTIONS;
            // No "next" from qualifying questions in this basic setup, submission is handled by submitOnboarding
            set({ currentStep: nextStep });
        },

        goToPreviousStep: () => {
            const { currentStep } = get();
            let prevStep = currentStep;
            if (currentStep === OnboardingStepV3.QUALIFYING_QUESTIONS) prevStep = OnboardingStepV3.PLATFORM_CONNECTIONS;
            else if (currentStep === OnboardingStepV3.PLATFORM_CONNECTIONS) prevStep = OnboardingStepV3.BRAND_NAME;
            set({ currentStep: prevStep });
        },

        submitOnboarding: async () => {
            const { currentStep, validateStep, createBrandOnboarding } = get();
            if (currentStep === OnboardingStepV3.QUALIFYING_QUESTIONS && validateStep(currentStep)) {
                const brand = await createBrandOnboarding();
                if (brand) {
                    // Navigation will be handled by the component using the hook,
                    // typically by checking if currentStep becomes COMPLETED.
                    // Example: history.push('/dashboard/maestro');
                    console.log("Onboarding submitted, brand created/updated:", brand);
                }
            } else if (!validateStep(currentStep)) {
                console.error("Validation failed for current step:", currentStep);
                // Optionally set an error for UI
            }
        },
        resetOnboardingState: () => {
            set(initialState);
        }
    }))
);

export default useOnboardingV3;
